/**
 * Linkage Rules Management Hook
 * Handles field linkage rules and dynamic field interactions
 */
import { useCallback, useRef } from 'react'
import { LinkageRule, LinkageActionType, DataSourceConfig } from '../types'

interface UseLinkageRulesProps {
  linkageRules?: LinkageRule[]
  dataSources?: DataSourceConfig[]
  getValues: () => Record<string, any>
  setValue: (
    name: string,
    value: any,
    options?: { shouldValidate?: boolean; shouldDirty?: boolean }
  ) => void
  clearErrors: (name?: string | string[]) => void
  reloadDataSource: (key: string) => Promise<void>
}

interface UseLinkageRulesReturn {
  applyLinkageRules: (triggerField: string) => void
  processLinkageRules: (
    watchedValues: Record<string, any>,
    prevWatchedValues: Record<string, any>
  ) => void
}

export const useLinkageRules = ({
  linkageRules = [],
  getValues,
  setValue,
  clearErrors,
  reloadDataSource
}: UseLinkageRulesProps): UseLinkageRulesReturn => {
  // Track processed linkage fields to prevent infinite loops
  const processedLinkageFieldsRef = useRef<Set<string>>(new Set())

  /**
   * Apply a single linkage rule based on trigger value
   */
  const applyLinkageRule = useCallback(
    (rule: LinkageRule, triggerValue: any) => {
      const allValues = getValues()

      // Check if condition is met (if condition exists)
      if (rule.condition && !rule.condition(triggerValue, allValues)) {
        return
      }

      // Prevent infinite loops by checking if we've already processed this field
      if (processedLinkageFieldsRef.current.has(rule.targetField)) {
        return
      }

      // Mark this field as processed
      processedLinkageFieldsRef.current.add(rule.targetField)

      // Suppress validation-flash on the target field: linkage-driven writes
      // should not trigger a "required" error, and any pre-existing error on
      // the target must be cleared so the UI settles cleanly.
      const setValueOptions = { shouldValidate: false, shouldDirty: true }

      try {
        switch (rule.action) {
          case LinkageActionType.CLEAR:
            clearErrors(rule.targetField)
            setValue(rule.targetField, null, setValueOptions)
            break

          case LinkageActionType.SET_VALUE: {
            let valueToSet = rule.value
            if (typeof valueToSet === 'function') {
              valueToSet = valueToSet(triggerValue, allValues)
            }
            clearErrors(rule.targetField)
            setValue(rule.targetField, valueToSet, setValueOptions)
            break
          }

          case LinkageActionType.RELOAD_DATA:
            if (rule.dataSourceKey) {
              reloadDataSource(rule.dataSourceKey)
            }
            break

          default:
            console.warn(`Unknown linkage action: ${rule.action}`)
        }
      } catch (error) {
        console.error(
          `Error applying linkage rule for field ${rule.targetField}:`,
          error
        )
      }
    },
    [getValues, setValue, clearErrors, reloadDataSource]
  )

  /**
   * Process linkage rules when watched values change
   */
  const processLinkageRules = useCallback(
    (
      watchedValues: Record<string, any>,
      prevWatchedValues: Record<string, any>
    ) => {
      if (linkageRules.length === 0) return

      // Clear processed fields set for this cycle
      processedLinkageFieldsRef.current.clear()

      // Check which fields have changed
      const changedFields = Object.keys(watchedValues).filter(field => {
        const currentValue = watchedValues[field]
        const prevValue = prevWatchedValues[field]

        // Compare values (handle different types)
        if (currentValue !== prevValue) {
          return true
        }

        // Handle array comparison
        if (Array.isArray(currentValue) && Array.isArray(prevValue)) {
          return JSON.stringify(currentValue) !== JSON.stringify(prevValue)
        }

        // Handle object comparison
        if (typeof currentValue === 'object' && typeof prevValue === 'object') {
          return JSON.stringify(currentValue) !== JSON.stringify(prevValue)
        }

        return false
      })

      // Apply linkage rules for changed fields
      changedFields.forEach(changedField => {
        const matchingRules = linkageRules.filter(
          rule => rule.triggerField === changedField
        )
        const triggerValue = watchedValues[changedField]

        matchingRules.forEach(rule => {
          applyLinkageRule(rule, triggerValue)
        })
      })
    },
    [linkageRules, applyLinkageRule]
  )

  /**
   * Apply linkage rules manually for a specific trigger field
   */
  const applyLinkageRules = useCallback(
    (triggerField: string) => {
      if (linkageRules.length === 0) return

      const triggerValue = getValues()[triggerField]
      const matchingRules = linkageRules.filter(
        rule => rule.triggerField === triggerField
      )

      // Clear processed fields set for this cycle
      processedLinkageFieldsRef.current.clear()

      // Apply each matching rule
      matchingRules.forEach(rule => {
        applyLinkageRule(rule, triggerValue)
      })
    },
    [linkageRules, getValues, applyLinkageRule]
  )

  return {
    applyLinkageRules,
    processLinkageRules
  }
}
